import { useState } from "react";
import { createFileRoute } from '@tanstack/react-router';
import { Mail, MessageSquare, Phone, MapPin, Send, Loader2, LucideIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ContentHeader } from "@/components/content";

export const Route = createFileRoute('/info/contact')({
  component: ContactPage,
  head: () => ({
    meta: [
      { title: 'Contact Us | Matricks' },
      { name: 'description', content: 'Have questions about Matricks? Want to learn more about our enterprise solutions? We\'d love to hear from you.' },
    ],
  }),
});

const iconMap: Record<string, LucideIcon> = {
  Mail,
  MessageSquare,
  Phone,
  MapPin,
};

const contactInfo = [
  {
    iconName: "Mail",
    title: "Email",
    description: "Send us an email anytime",
    value: "support@matricks.ai",
    href: "mailto:support@matricks.ai",
  },
  {
    iconName: "MessageSquare",
    title: "Live Chat",
    description: "Chat with our support team",
    value: "Available 9am-6pm EST",
    href: "#",
  },
  {
    iconName: "Phone",
    title: "Phone",
    description: "Call us during business hours",
    value: "+1 (555) 123-4567",
    href: "tel:+15551234567",
  },
  {
    iconName: "MapPin",
    title: "Office",
    description: "Visit us in person",
    value: "San Francisco, CA",
    href: "#",
  },
];

function ContactPage() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Simulate form submission
    await new Promise((resolve) => setTimeout(resolve, 1500));

    setIsSubmitting(false);
    setIsSubmitted(true);
  };

  return (
    <div className="py-16 sm:py-24">
      <div className="container px-6">
        {/* Header */}
        <ContentHeader
          title="Get in touch"
          description="Have questions about Matricks? Want to learn more about our enterprise solutions? We'd love to hear from you."
          centered
        />

        <div className="mt-16 grid grid-cols-1 gap-12 lg:grid-cols-2">
          {/* Contact Form */}
          <div className="rounded-2xl border bg-card p-8">
            <h2 className="text-xl font-semibold">Send us a message</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Fill out the form below and we'll get back to you within 24 hours.
            </p>

            {isSubmitted ? (
              <div className="mt-8 rounded-lg bg-green-500/10 p-6 text-center">
                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-500/20">
                  <Send className="h-6 w-6 text-green-500" />
                </div>
                <h3 className="mt-4 font-semibold">Message sent!</h3>
                <p className="mt-2 text-sm text-muted-foreground">
                  Thank you for reaching out. We'll get back to you soon.
                </p>
                <Button
                  className="mt-4"
                  variant="outline"
                  onClick={() => setIsSubmitted(false)}
                >
                  Send another message
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="mt-8 space-y-6">
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <div>
                    <label htmlFor="firstName" className="text-sm font-medium">
                      First Name
                    </label>
                    <Input
                      id="firstName"
                      name="firstName"
                      required
                      className="mt-2"
                      placeholder="John"
                    />
                  </div>
                  <div>
                    <label htmlFor="lastName" className="text-sm font-medium">
                      Last Name
                    </label>
                    <Input
                      id="lastName"
                      name="lastName"
                      required
                      className="mt-2"
                      placeholder="Doe"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="email" className="text-sm font-medium">
                    Work Email
                  </label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    required
                    className="mt-2"
                    placeholder="john@company.com"
                  />
                </div>

                <div>
                  <label htmlFor="company" className="text-sm font-medium">
                    Company
                  </label>
                  <Input
                    id="company"
                    name="company"
                    className="mt-2"
                    placeholder="Acme Inc."
                  />
                </div>

                <div>
                  <label htmlFor="subject" className="text-sm font-medium">
                    Subject
                  </label>
                  <select
                    id="subject"
                    name="subject"
                    required
                    className="mt-2 w-full rounded-md border bg-background px-3 py-2 text-sm"
                  >
                    <option value="">Select a topic</option>
                    <option value="sales">Sales Inquiry</option>
                    <option value="support">Technical Support</option>
                    <option value="partnership">Partnership</option>
                    <option value="press">Press & Media</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="text-sm font-medium">
                    Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows={4}
                    className="mt-2 w-full rounded-md border bg-background px-3 py-2 text-sm resize-none"
                    placeholder="Tell us how we can help..."
                  />
                </div>

                <Button type="submit" className="w-full" disabled={isSubmitting}>
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      Send Message
                      <Send className="ml-2 h-4 w-4" />
                    </>
                  )}
                </Button>
              </form>
            )}
          </div>

          {/* Contact Info */}
          <div className="space-y-8">
            <div>
              <h2 className="text-xl font-semibold">Other ways to reach us</h2>
              <p className="mt-2 text-sm text-muted-foreground">
                Prefer a different method? No problem. Here are all the ways you can get in touch.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-1">
              {contactInfo.map((item) => (
                <a
                  key={item.title}
                  href={item.href}
                  className="flex items-start gap-4 rounded-xl border bg-card p-6 transition-colors hover:border-primary/50"
                >
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                    {(() => {
                      const Icon = iconMap[item.iconName] || Mail;
                      return <Icon className="h-5 w-5 text-primary" />;
                    })()}
                  </div>
                  <div>
                    <h3 className="font-medium">{item.title}</h3>
                    <p className="text-sm text-muted-foreground">{item.description}</p>
                    <p className="mt-1 text-sm font-medium text-primary">{item.value}</p>
                  </div>
                </a>
              ))}
            </div>

            {/* Enterprise CTA */}
            <div className="rounded-xl border bg-primary/5 p-6">
              <h3 className="font-semibold">Enterprise Solutions</h3>
              <p className="mt-2 text-sm text-muted-foreground">
                Looking for a custom solution for your organization? Our enterprise team is ready
                to help you design the perfect integration strategy.
              </p>
              <Button className="mt-4" variant="outline" asChild>
                <a href="mailto:enterprise@matricks.ai">Contact Enterprise Sales</a>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
